/**
 * Flattens a BYOD table response's per-row `entities_attributes` object into
 * top-level row keys, mirroring `transformTableData` in
 * src/utils/analytics-genesis.ts (which does the same for the people/touchpoint
 * tables, using the singular `entity_attributes`).
 *
 * The backend, when a widget's `entity_filter_config` is set, returns:
 *  - a placeholder column `{key: 'entities_attributes', label: 'Entities Attributes'}`, and
 *  - a per-row `entities_attributes: Record<string, unknown>` keyed by
 *    `entityType/attribute.subfield` (e.g. `candidate/address.city`).
 *
 * We hoist each attribute onto the row (so the table can read it via
 * `data[key]`), drop the placeholder column (otherwise the raw object renders as
 * JSON), and return the union of attribute keys as `all_attributes_columns` —
 * the available options for the entity-variable column picker.
 *
 * MUST run AFTER `normalizeDecimalValues` (which rebuilds the object graph), so
 * mutating rows in place here is safe and any Decimal artifacts inside attribute
 * values are already resolved before they're hoisted. It is a no-op when no row
 * carries a non-empty `entities_attributes` and there's no placeholder column.
 */

const ENTITY_ATTRIBUTES_KEY = 'entities_attributes';

type TableColumn = {key: string; label: string};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function flattenTableEntityAttributes<T>(table: T): T {
  if (!isPlainObject(table) || !Array.isArray(table.rows)) {
    return table;
  }

  const attributeKeys = new Set<string>();
  for (const row of table.rows) {
    if (!isPlainObject(row)) {
      continue;
    }
    const attrs = row[ENTITY_ATTRIBUTES_KEY];
    if (isPlainObject(attrs)) {
      for (const key of Object.keys(attrs)) {
        attributeKeys.add(key);
      }
      Object.assign(row, attrs);
      delete row[ENTITY_ATTRIBUTES_KEY];
    }
  }

  const hasPlaceholderColumn =
    Array.isArray(table.columns) &&
    (table.columns as TableColumn[]).some(
      (col) => col?.key === ENTITY_ATTRIBUTES_KEY,
    );

  // Nothing to do — leave the response untouched.
  if (attributeKeys.size === 0 && !hasPlaceholderColumn) {
    return table;
  }

  const columns = Array.isArray(table.columns)
    ? (table.columns as TableColumn[]).filter(
        (col) => col?.key !== ENTITY_ATTRIBUTES_KEY,
      )
    : table.columns;

  return {
    ...table,
    columns,
    all_attributes_columns: Array.from(attributeKeys),
  };
}
